import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";

const isNomineeBelow18 = (nomineeDateOfBirth: string): any => {
  const dob = new Date(nomineeDateOfBirth);
  const today = new Date();

  const diffInMs = today.getTime() - dob.getTime();
  const ageInYears = diffInMs / (1000 * 60 * 60 * 24 * 365.25);

  return ageInYears;
};

const isGuardianRequired = (relationShip) => {
  // Implement your logic to check if guardian is required based on the relationship
  const requiredRelationships = [
    "Son",
    "Daughter",
    "Grand son",
    "Grand daughter",
    "Others",
    "Brother",
    "Sister",
  ];
  return (
    relationShip.length > 0 &&
    requiredRelationships.includes(relationShip[0].value)
  );
};

export const schema = Yup.object().shape({
  nominees: Yup.array().of(
    Yup.object().shape({
      nomineeFullName: Yup.string()
        .required("Nominee Name is Required")
        .max(50, "Max 50 characters is allowed")
        .matches(/^[a-zA-Z ]*$/, "Enter Valid Name")
        .min(3, "Minimum 3 characters is required")
        .matches(/^(?!.*\s{2,}).*$/, "Consecutive spaces are not allowed")
        .transform((value: any) => value && value.toUpperCase()),

      nomineeEmail: Yup.string()
        .required("Email is Required")
        .max(50, "Max 50 characters is allowed")
        .matches(
          /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          "Enter Valid Email"
        ),

      nomineeDateofBirth: Yup.string()
        .required("Nominee Date of Birth is required")
        .test({
          name: "valid-age-and-guardian-check",
          message: "Invalid Date of Birth",
          test: function (value) {
            if (!value) return true;

            const { relationShip } = this.parent;
            const dob: any = new Date(value);
            const now = new Date();

            if (isNaN(dob)) {
              return this.createError({
                message: "Enter Valid Date",
                path: "nomineeDateofBirth",
              });
            }

            const ageInYears =
              (now.getTime() - dob.getTime()) / (1000 * 60 * 60 * 24 * 365.25);

            if (ageInYears < 1 || ageInYears > 100) {
              return this.createError({
                message: "Age must be between 1 and 100",
                path: "nomineeDateofBirth",
              });
            }

            // Check guardian requirement if age < 18
            if (ageInYears < 18 && relationShip) {
              const guardianRequired = relationShip?.guardian_req === true;

              if (!guardianRequired) {
                return this.createError({
                  message:
                    "Guardian is required for this relationship as age is below 18",
                  path: "relationShip", // ✅ Triggers error on relationShip field
                });
              }
            }

            return true;
          },
        }),

      nomineeMobileNumber: Yup.string()
        .required("Mobile Number is Required")
        .max(10)

        .matches(/^[0-9]{10}$/, "Enter Valid Number"),

      percentageofShare: Yup.string()
        .required("Percentage is Required")
        .max(3)
        .matches(/^[0-9]+%*$/, "Enter Valid Percentage"),
      nomineeAddress: Yup.string()
        .required("Address is Required")
        .min(10, "Minimum 10 characters is required"),

      nomineeAddressPinCode: Yup.string()
        .required("Pincode is Required")
        .max(6)
        .min(6, "Pincode must be 6 digits")
        .matches(/^(?!.*\s{2,}).*$/, "Consecutive spaces are not allowed"),
      nomineeAddressCity: Yup.string()
        .required("City is Required")
        .min(3, "Minimum 3 characters is required")
        .matches(/^(?!.*\s{2,}).*$/, "Consecutive spaces are not allowed")
        .max(50, "Max 50 characters is allowed"),
      nomineeAddressState: Yup.object().required("State is Required"),

      relationShip: Yup.object().required("Please select an option"),
      relationshipIfOthers: Yup.string().when(
        "relationShip",
        (relationShip: any, schema: any) => {
          // console.log("relationShip", relationShip);
          if (
            relationShip.length &&
            relationShip[0] &&
            relationShip[0].value === "Others"
          ) {
            return schema
              .required("Relationship is Required")
              .matches(/^[a-zA-Z ]*$/, "Enter Valid Relation")
              .max(50)
              .min(3, "Minimum 3 characters is required")
              .matches(/^(?!.*\s{2,}).*$/, "Consecutive spaces are not allowed")
              .transform((value: any) => value && value.toUpperCase());
          }

          return schema;
        }
      ),

      gaurdianFullName: Yup.string()
        .matches(/^[a-zA-Z ]*$/, "Enter Valid Name")
        .max(50, "Max 50 characters is allowed")
        .when(
          ["relationShip", "nomineeDateofBirth"],
          (relationShip: any, schema: any) => {
            //  console.log("relationShip", relationShip);
            if (
              isNomineeBelow18(relationShip[1]) < 18 &&
              isGuardianRequired(relationShip)
            ) {
              return schema
                .required("Please Enter Guardian Name")
                .min(3, "Minimum 3 characters is required")
                .matches(
                  /^(?!.*\s{2,}).*$/,
                  "Consecutive spaces are not allowed"
                )
                .matches(/^[a-zA-Z ]*$/, "Enter Valid Relation");
              // .transform((value: any) => value && value.toUpperCase());
            }
          }
        ),
      gaurdianDateofBirth: Yup.string().when(
        ["relationShip", "nomineeDateofBirth"],
        (relationShip: any, schema: any) => {
          // console.log("relationShip", relationShip);
          if (
            isNomineeBelow18(relationShip[1]) < 18 &&
            isGuardianRequired(relationShip)
          ) {
            return schema
              .required("Guardian Date of Birth is a required")
              .test({
                name: "valid-age",
                message: "Age must be above 18 years",
                test: function (value: any) {
                  if (!value) return true; // Let the required validation handle empty values

                  const currentDate: any = new Date();
                  const dob: any = new Date(value);

                  if (isNaN(dob)) {
                    return this.createError({
                      message: "Enter Valid Date", // Custom error message for invalid date
                      path: "gaurdianDateofBirth", // Field name
                    });
                  }

                  const ageInYears =
                    (currentDate - dob) / (365.25 * 24 * 60 * 60 * 1000); // Age in years

                  return ageInYears >= 18 && ageInYears <= 100;
                },
              });
          }
        }
      ),

      gaurdianEmail: Yup.string()
        .matches(
          /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          "Enter Valid Email"
        )
        .max(50, "Max 50 characters is allowed")
        .when(
          ["relationShip", "nomineeDateofBirth"],
          (relationShip: any, schema: any) => {
            // console.log("relationShip", relationShip);
            if (
              isNomineeBelow18(relationShip[1]) < 18 &&
              isGuardianRequired(relationShip)
            ) {
              return schema.required("Guardian Email is required");
            }
          }
        ),

      gaurdianMobileNumber: Yup.string()
        .matches(/^[0-9]{10}$/, "Enter Valid Number")
        .when(
          ["relationShip", "nomineeDateofBirth"],
          (relationShip: any, schema: any) => {
            // console.log(
            //   "isNomineeBelow18(relationShip[1])",
            //   isNomineeBelow18(relationShip[1])
            // );
            if (
              isNomineeBelow18(relationShip[1]) < 18 &&
              isGuardianRequired(relationShip)
            ) {
              return schema.required("Guardian Phone Number is required");
            }
          }
        ),

      gaurdianRelationWithNominee: Yup.object().when(
        ["relationShip", "nomineeDateofBirth"],
        (relationShip: any, schema: any) => {
          // console.log("relationShip", relationShip);
          if (
            isNomineeBelow18(relationShip[1]) < 18 &&
            isGuardianRequired(relationShip)
          ) {
            return schema.required(
              "Guardian Relation With Nominee is  required"
            );
          }
        }
      ),
      gaurdianAddress: Yup.string().when(
        ["relationShip", "nomineeDateofBirth"],
        (relationShip: any, schema: any) => {
          // console.log("relationShip", relationShip);
          if (
            isNomineeBelow18(relationShip[1]) < 18 &&
            isGuardianRequired(relationShip)
          ) {
            return schema
              .required("Guardian Address is required")
              .min(10, "Minimum 10 characters is required")
              .max(250, "Max 250 characters is allowed");
          }
        }
      ),
      gaurdianAddressPinCode: Yup.string().when(
        ["relationShip", "nomineeDateofBirth"],
        (relationShip: any, schema: any) => {
          // console.log("relationShip", relationShip);
          if (
            isNomineeBelow18(relationShip[1]) < 18 &&
            isGuardianRequired(relationShip)
          ) {
            return schema
              .required("Guardian PinCode is required")
              .max(6)
              .min(6, "Pincode must be 6 digits")
              .matches(/^(?!.*\s{2,}).*$/, "Consecutive spaces are not allowed")
              .max(6);
          }
        }
      ),
      gaurdianAddressCity: Yup.string().when(
        ["relationShip", "nomineeDateofBirth"],
        (relationShip: any, schema: any) => {
          // console.log("relationShip", relationShip);
          if (
            isNomineeBelow18(relationShip[1]) < 18 &&
            isGuardianRequired(relationShip)
          ) {
            return schema
              .required("Guardian City is required")
              .min(3, "Minimum 3 characters is required")
              .matches(/^(?!.*\s{2,}).*$/, "Consecutive spaces are not allowed")
              .max(50, "Max 50 characters is allowed");
          }
        }
      ),
      gaurdianAddressState: Yup.object().when(
        ["relationShip", "nomineeDateofBirth"],
        (relationShip: any, schema: any) => {
          // console.log("relationShip", relationShip);
          if (
            isNomineeBelow18(relationShip[1]) < 18 &&
            isGuardianRequired(relationShip)
          ) {
            return schema.required("Guardian State is required");
          }
        }
      ),

      nomineeIDProof: Yup.string().required("Nominee ID Proof is required"),
      nomineeIDProofNo: Yup.string().when(
        "nomineeIDProof",
        (nomineeIDProof: any, schema: any) => {
          if (nomineeIDProof[0] === "Aadhaar Card")
            return schema
              .required("Nominee ID Proof Number is required")
              .length(4, "Enter Last 4 digits of Aadhaar")
              .matches(/^([0-9])/, "Enter Last 4 digits of Aadhaar")
              .transform((value: any) => value && value.toUpperCase());

          if (nomineeIDProof[0] === "Pan Card")
            return schema
              .required("Nominee ID Proof Number is required")
              .length(10, "Enter Valid Pan Number")
              .matches(
                /^([a-zA-Z]){5}([0-9]){4}([a-zA-Z]){1}?$/,
                "Enter Valid Pan Number"
              )
              .transform((value: any) => value && value.toUpperCase());
          if (nomineeIDProof[0] === "Passport")
            return schema
              .required("Nominee ID Proof Number is required")
              .max(30, "Passport Number should be at most 30 characters")
              .matches(/^[a-zA-Z0-9 ]*$/, "Only alphabets, numbers are allowed")
              .matches(/^(?!.*\s{1,}).*$/, "Spaces are not allowed")
              .transform((value: any) => value && value.toUpperCase());
          if (nomineeIDProof[0] === "Driving Licence")
            return schema
              .required("Nominee ID Proof Number is required")
              .max(30, "Driving Licence should be at most 30 characters")
              .matches(/^[a-zA-Z0-9 ]*$/, "Only alphabets, numbers are allowed")
              .matches(/^(?!.*\s{2,}).*$/, "Consecutive spaces are not allowed")
              .transform((value: any) => value && value.toUpperCase());

          return schema;
        }
      ),
      // nomineePanCardNo: Yup.string().when("nomineeIDProof", {
      //   is: "panCardNo",
      //   then(schema) {
      //     return schema.required(" Nominee ID Number is a required");
      //   },
      // }),
    })
  ),
});
function dayjs(arg0: string): any {
  throw new Error("Function not implemented.");
}
